import type {CadEntityKind} from '../../document';
import type {
    EntityPatch,
    HistoryApplyDirection,
    HistoryEntitySnapshot,
} from '../types';

/**
 * 历史补丁应用输入。
 */
export interface HistoryPatchApplyInput<TSnapshot = unknown, TContext = unknown> {
    /** 应用方向。 */
    readonly direction: HistoryApplyDirection;
    /** 待应用补丁。 */
    readonly patch: EntityPatch<TSnapshot>;
    /** 本次应恢复到的快照。 */
    readonly snapshot: HistoryEntitySnapshot<TSnapshot>;
    /** 调用方上下文；Phase 1 测试可注入假实体存储。 */
    readonly context: TContext;
}

/**
 * 历史领域适配器。
 *
 * 设计说明：
 * 1. Phase 1 的 `PatchApplier` 只能依赖该抽象；
 * 2. 具体 Sketch / Feature / Assembly 适配器必须在后续阶段分别实现；
 * 3. adapter 不应持有 web-client、Dexie、OPFS 或渲染层依赖。
 */
export interface HistoryDomainAdapter<TSnapshot = unknown, TContext = unknown> {
    /** adapter 稳定 ID。 */
    readonly adapterId: string;
    /** adapter 支持的实体类型。 */
    readonly kind: CadEntityKind;
    /**
     * 应用快照。
     *
     * @param input 补丁应用输入。
     */
    readonly applySnapshot: (input: HistoryPatchApplyInput<TSnapshot, TContext>) => void;
    /**
     * 判断两个快照是否语义相等。
     *
     * 未提供该函数时，历史核心会保守认为 update patch 有变化，避免误丢事务。
     *
     * @param left 左侧快照。
     * @param right 右侧快照。
     * @param patch 当前补丁。
     * @returns 是否语义相等。
     */
    readonly areSnapshotsEqual?: (
        left: HistoryEntitySnapshot<TSnapshot>,
        right: HistoryEntitySnapshot<TSnapshot>,
        patch: EntityPatch<TSnapshot>,
    ) => boolean;
}

/**
 * 历史领域适配器注册表。
 */
export interface HistoryDomainAdapterRegistry {
    /**
     * 注册 adapter。
     *
     * @param adapter 历史领域适配器。
     */
    readonly register: (adapter: HistoryDomainAdapter) => void;
    /**
     * 查询 adapter。
     *
     * @param adapterId adapter ID。
     * @returns adapter；不存在时返回 `undefined`。
     */
    readonly get: (adapterId: string) => HistoryDomainAdapter | undefined;
    /**
     * 根据 ID 断言 adapter 存在。
     *
     * @param adapterId adapter ID。
     * @returns adapter。
     */
    readonly assert: (adapterId: string) => HistoryDomainAdapter;
    /**
     * 清空注册表。
     */
    readonly clear: () => void;
}

/**
 * 创建历史领域适配器注册表。
 *
 * @param initialAdapters 初始适配器。
 * @returns adapter 注册表。
 */
export function createHistoryDomainAdapterRegistry(
    initialAdapters: readonly HistoryDomainAdapter[] = [],
): HistoryDomainAdapterRegistry {
    const adapterMap = new Map<string, HistoryDomainAdapter>();

    const register = (adapter: HistoryDomainAdapter): void => {
        if (adapterMap.has(adapter.adapterId)) {
            throw new Error(`历史领域适配器已注册：${adapter.adapterId}`);
        }

        adapterMap.set(adapter.adapterId, adapter);
    };

    initialAdapters.forEach(register);

    return {
        register,
        get(adapterId) {
            return adapterMap.get(adapterId);
        },
        assert(adapterId) {
            const adapter = adapterMap.get(adapterId);

            if (!adapter) {
                throw new Error(`未找到历史领域适配器：${adapterId}`);
            }

            return adapter;
        },
        clear() {
            adapterMap.clear();
        },
    };
}

